import { serializeConfigForm } from "./config/form-utils.ts";

/**
 * LLM Connect — bible §13 provider form.
 *
 * Simple connection data, model selection, and an availability test.
 * Added models can be assigned to roles or set as the platform default.
 * There is no per-agent destination.
 */

export type LlmConnectTab = "providers" | "local" | "settings";

export type LlmConnectDestination = "role" | "default" | "unassigned";

export type LlmProviderCard = {
  id: string;
  name: string;
  connected: boolean;
  hasKey: boolean;
  baseUrl: string;
  modelCount: number;
};

export type LlmDiscoveredModel = {
  id: string;
  name: string;
  provider: string;
  enabled: boolean;
};

export type LlmConnectState = {
  tab: LlmConnectTab;
  selectedProviderId: string | null;
  apiKeyDraft: string;
  testing: boolean;
  testMessage: string | null;
  testOk: boolean | null;
  saving: boolean;
  error: string | null;
  destination: LlmConnectDestination;
};

export const KNOWN_LLM_PROVIDERS: Array<{
  id: string;
  name: string;
  baseUrl: string;
  api?: string;
}> = [
  {
    id: "minimax-portal",
    name: "MiniMax",
    baseUrl: "https://api.minimax.io/anthropic",
    api: "anthropic-messages",
  },
  { id: "openai", name: "OpenAI", baseUrl: "https://api.openai.com/v1", api: "openai-completions" },
  {
    id: "anthropic",
    name: "Anthropic",
    baseUrl: "https://api.anthropic.com",
    api: "anthropic-messages",
  },
  {
    id: "google",
    name: "Google Gemini",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta",
  },
  {
    id: "openrouter",
    name: "OpenRouter",
    baseUrl: "https://openrouter.ai/api/v1",
    api: "openai-completions",
  },
  {
    id: "groq",
    name: "Groq",
    baseUrl: "https://api.groq.com/openai/v1",
    api: "openai-completions",
  },
];

export function emptyLlmConnectState(): LlmConnectState {
  return {
    tab: "providers",
    selectedProviderId: "minimax-portal",
    apiKeyDraft: "",
    testing: false,
    testMessage: null,
    testOk: null,
    saving: false,
    error: null,
    destination: "unassigned",
  };
}

export function providersFromConfig(config: Record<string, unknown> | null): LlmProviderCard[] {
  const configured = (config?.models as { providers?: Record<string, unknown> } | undefined)
    ?.providers;
  const seen = new Set<string>();
  const cards: LlmProviderCard[] = [];
  for (const known of KNOWN_LLM_PROVIDERS) {
    const raw = configured?.[known.id];
    cards.push(toCard(known.id, known.name, known.baseUrl, raw));
    seen.add(known.id);
  }
  if (configured) {
    for (const [id, raw] of Object.entries(configured)) {
      if (seen.has(id)) {
        continue;
      }
      const name = id
        .split(/[-_]/g)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
      const baseUrl =
        raw && typeof raw === "object" && typeof (raw as { baseUrl?: unknown }).baseUrl === "string"
          ? (raw as { baseUrl: string }).baseUrl
          : "";
      cards.push(toCard(id, name, baseUrl, raw));
    }
  }
  return cards;
}

function toCard(id: string, name: string, fallbackUrl: string, raw: unknown): LlmProviderCard {
  const record = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
  const apiKey = typeof record?.apiKey === "string" ? record.apiKey.trim() : "";
  const hasKey = apiKey.length > 0;
  const models = Array.isArray(record?.models) ? record.models : [];
  return {
    id,
    name,
    connected: hasKey,
    hasKey,
    baseUrl:
      typeof record?.baseUrl === "string" && record.baseUrl.trim()
        ? record.baseUrl.trim()
        : fallbackUrl,
    modelCount: models.length,
  };
}

export function discoveredModelsForProvider(
  providerId: string,
  catalog: Array<{ id: string; name?: string; provider: string }>,
  config: Record<string, unknown> | null,
): LlmDiscoveredModel[] {
  const configured = (
    (
      config?.models as {
        providers?: Record<string, { models?: Array<{ id?: string; name?: string }> }>;
      }
    )?.providers?.[providerId]?.models ?? []
  ).filter((model) => typeof model.id === "string" && model.id.trim());
  const enabled = new Set(configured.map((model) => model.id as string));
  const fromCatalog = catalog
    .filter((model) => model.provider === providerId)
    .map((model) => ({
      id: model.id,
      name: model.name?.trim() || model.id,
      provider: providerId,
      enabled: enabled.size === 0 ? true : enabled.has(model.id),
    }));
  const seen = new Set(fromCatalog.map((model) => model.id));
  const extras = configured
    .filter((model) => !seen.has(model.id as string))
    .map((model) => ({
      id: model.id as string,
      name: model.name?.trim() || (model.id as string),
      provider: providerId,
      enabled: true,
    }));
  return [...fromCatalog, ...extras];
}

export function providerStatusLabel(card: LlmProviderCard): string {
  if (card.connected) {
    return "Connected";
  }
  return "Not connected";
}

export function knownProviderById(id: string) {
  return KNOWN_LLM_PROVIDERS.find((provider) => provider.id === id);
}

type ConfigClient = {
  request: (method: string, params: Record<string, unknown>) => Promise<unknown>;
};

export type LlmConnectHost = {
  client: ConfigClient | null;
  connected: boolean;
  configForm: Record<string, unknown> | null;
  llmConnect: LlmConnectState;
  chatModelCatalog: Array<{ id: string; name?: string; provider: string }>;
};

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

async function mutateModelsConfig(
  host: LlmConnectHost,
  mutate: (config: Record<string, unknown>) => void,
): Promise<Record<string, unknown> | null> {
  if (!host.client || !host.connected) {
    return null;
  }
  const snapshot = (await host.client.request("config.get", {})) as {
    hash?: string;
    config?: Record<string, unknown>;
  };
  if (!snapshot.hash || !snapshot.config) {
    throw new Error("Config hash missing; reload and retry.");
  }
  const current = JSON.parse(JSON.stringify(snapshot.config)) as Record<string, unknown>;
  mutate(current);
  await host.client.request("config.set", {
    raw: serializeConfigForm(current),
    baseHash: snapshot.hash,
  });
  host.configForm = current;
  return current;
}

function ensureProvider(
  config: Record<string, unknown>,
  providerId: string,
): Record<string, unknown> {
  const models = asRecord(config.models);
  const providers = asRecord(models.providers);
  const existing = asRecord(providers[providerId]);
  const known = knownProviderById(providerId);
  const next = {
    baseUrl:
      typeof existing.baseUrl === "string" && existing.baseUrl.trim()
        ? existing.baseUrl
        : (known?.baseUrl ?? "https://api.openai.com/v1"),
    models: Array.isArray(existing.models) ? existing.models : [],
    ...existing,
    ...(known?.api && !existing.api ? { api: known.api } : {}),
  };
  providers[providerId] = next;
  models.providers = providers;
  config.models = models;
  return next;
}

export async function saveLlmProviderKey(host: LlmConnectHost): Promise<boolean> {
  const providerId = host.llmConnect.selectedProviderId;
  const apiKey = host.llmConnect.apiKeyDraft.trim();
  if (!providerId || !apiKey) {
    return false;
  }
  host.llmConnect = { ...host.llmConnect, saving: true, error: null };
  try {
    await mutateModelsConfig(host, (config) => {
      const provider = ensureProvider(config, providerId);
      provider.apiKey = apiKey;
    });
    host.llmConnect = { ...host.llmConnect, saving: false, apiKeyDraft: "" };
    return true;
  } catch (err) {
    host.llmConnect = { ...host.llmConnect, saving: false, error: String(err) };
    return false;
  }
}

export async function testLlmProvider(host: LlmConnectHost): Promise<void> {
  if (!host.client || !host.connected || !host.llmConnect.selectedProviderId) {
    return;
  }
  host.llmConnect = { ...host.llmConnect, testing: true, testMessage: null, testOk: null };
  try {
    const listed = (await host.client.request("models.list", {})) as {
      models?: Array<{ id: string; provider: string }>;
    };
    const models = Array.isArray(listed?.models) ? listed.models : [];
    const mine = models.filter((model) => model.provider === host.llmConnect.selectedProviderId);
    host.llmConnect = {
      ...host.llmConnect,
      testing: false,
      testOk: true,
      testMessage:
        mine.length > 0
          ? `Available — ${mine.length} model${mine.length === 1 ? "" : "s"} discovered.`
          : "The provider accepted the request. No models were discovered yet.",
    };
  } catch (err) {
    host.llmConnect = {
      ...host.llmConnect,
      testing: false,
      testOk: false,
      testMessage: String(err),
    };
  }
}

export async function setPlatformDefaultModel(
  host: LlmConnectHost,
  modelRef: string,
): Promise<boolean> {
  if (!modelRef.trim()) {
    return false;
  }
  try {
    await mutateModelsConfig(host, (config) => {
      const agents = asRecord(config.agents);
      const defaults = asRecord(agents.defaults);
      defaults.model = modelRef.trim();
      agents.defaults = defaults;
      config.agents = agents;
    });
    return true;
  } catch (err) {
    host.llmConnect = { ...host.llmConnect, error: String(err) };
    return false;
  }
}

export async function toggleProviderModel(
  host: LlmConnectHost,
  model: LlmDiscoveredModel,
  enabled: boolean,
): Promise<boolean> {
  try {
    await mutateModelsConfig(host, (config) => {
      const provider = ensureProvider(config, model.provider);
      const list = Array.isArray(provider.models) ? [...(provider.models as object[])] : [];
      const next = enabled
        ? list.some(
            (entry) =>
              entry && typeof entry === "object" && (entry as { id?: string }).id === model.id,
          )
          ? list
          : [...list, { id: model.id, name: model.name }]
        : list.filter(
            (entry) =>
              !(entry && typeof entry === "object" && (entry as { id?: string }).id === model.id),
          );
      provider.models = next;
    });
    return true;
  } catch (err) {
    host.llmConnect = { ...host.llmConnect, error: String(err) };
    return false;
  }
}
